import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, ArrowsRightLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import SearchableSelect from '../SearchableSelect';
import weighingService from '@/services/weighing';
import { isPeseeEditable, requiresAdminToEditPesee } from '@/services/historyService';
import { getWeighingProcessBadgeClass, getWeighingProcessLabel } from '@/utils/weighingProcess';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS, PLATFORM_ROLES, OPERATIONAL_ROLES } from '@/types/permissions';

/** Poids net = brut − (tare + sacs acceptés) */
const computePoidsNet = (brut: string, tare: string, acceptes: string): string => {
  const poidsBrut = parseFloat(brut) || 0;
  const poidsTare = parseFloat(tare) || 0;
  const poidsSacs = parseFloat(acceptes) || 0;
  const net = poidsBrut - (poidsTare + poidsSacs);
  return Number.isFinite(net) ? String(Number(net.toFixed(2))) : '';
};

/** Rôles autorisés à modifier une pesée en cours */
const MODIFY_PESSE_ROLES = [
  PLATFORM_ROLES.PLATFORM_SUPER_ADMIN,
  PLATFORM_ROLES.PLATFORM_SUPPORT,
  PLATFORM_ROLES.PLATFORM_ADMIN,
  OPERATIONAL_ROLES.SITE_MANAGER,
  OPERATIONAL_ROLES.SITE_SUPERVISOR,
  OPERATIONAL_ROLES.TENANT_ADMIN,
  OPERATIONAL_ROLES.TENANT_MANAGER,
  OPERATIONAL_ROLES.TENANT_OWNER,
  OPERATIONAL_ROLES.SITE_ADMIN,
] as const;

/** Rôles admin — seuls autorisés à modifier une pesée COMPLETEE / ACHEVEE */
const ADMIN_MODIFY_COMPLETED_ROLES = [
  PLATFORM_ROLES.PLATFORM_SUPER_ADMIN,
  PLATFORM_ROLES.PLATFORM_ADMIN,
  OPERATIONAL_ROLES.TENANT_OWNER,
  OPERATIONAL_ROLES.TENANT_ADMIN,
  OPERATIONAL_ROLES.SITE_ADMIN,
] as const;

const parseOptionalFloat = (value: string | number | undefined | null): number | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;
  const parsed = parseFloat(String(value));
  return Number.isNaN(parsed) ? undefined : parsed;
};

const parseOptionalInt = (value: string | number | undefined | null): number | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;
  const parsed = parseInt(String(value), 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const formatValidationErrors = (errors: Record<string, unknown>): string => {
  return Object.entries(errors)
    .map(([field, messages]) => {
      const text = Array.isArray(messages) ? messages.join(', ') : String(messages);
      return `${field}: ${text}`;
    })
    .join('\n');
};

const formatApiError = (error: unknown): string => {
  const err = error as { response?: { data?: Record<string, unknown> }; message?: string };
  const data = err?.response?.data;
  if (!data) return err?.message || 'Erreur inconnue';

  if (data.message && data.errors && typeof data.errors === 'object') {
    const details = formatValidationErrors(data.errors as Record<string, unknown>);
    return details ? `${data.message}\n${details}` : String(data.message);
  }
  if (data.message) return String(data.message);
  if (typeof data === 'object') return formatValidationErrors(data as Record<string, unknown>) || 'Erreur lors de la sauvegarde';
  return 'Erreur lors de la sauvegarde';
};

const toFormValue = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '';
  return String(value);
};

/** Capacité conditionnement (kg/sac), défaut 50 */
const getConditionnementCapacite = (
  conditionnementId: string | number | undefined,
  conditionnements: Array<{ id: number | string; poids_tare?: number }>,
  fallback = 50,
): number => {
  if (conditionnementId === undefined || conditionnementId === null || conditionnementId === '') {
    return fallback;
  }
  const found = conditionnements.find((c) => c.id.toString() === conditionnementId.toString());
  const cap = found?.poids_tare;
  return cap != null && Number(cap) > 0 ? Number(cap) : fallback;
};

/**
 * Poids sacs acceptés :
 * - si sains/recond > 0 → (sains + recond×4) × capacité
 * - sinon si déclarés / poids déclarés → calcul à partir des déclarés
 * - sinon conserve le poids déjà connu (évite d'écraser à 0 à l'ouverture)
 */
const computePoidsAcceptes = (opts: {
  nbSacsSains: string | number | undefined;
  nbSacsRecond: string | number | undefined;
  nbSacsDeclares: string | number | undefined;
  conditionnementCapacite: number;
  poidsAcceptesExistant?: string | number | null;
  poidsDeclares?: string | number | null;
  preferExisting?: boolean;
}): string => {
  const sains = parseInt(String(opts.nbSacsSains ?? ''), 10) || 0;
  const recond = parseInt(String(opts.nbSacsRecond ?? ''), 10) || 0;
  const declares = parseInt(String(opts.nbSacsDeclares ?? ''), 10) || 0;
  const FACTEUR_RECONDITIONNEMENT = 4;
  const capacite = opts.conditionnementCapacite || 50;
  const existing = parseFloat(String(opts.poidsAcceptesExistant ?? ''));
  const hasExisting = !Number.isNaN(existing) && existing > 0;

  if (sains > 0 || recond > 0) {
    return String((sains + recond * FACTEUR_RECONDITIONNEMENT) * capacite);
  }

  // À l'ouverture : garder le poids stocké s'il est déjà renseigné
  if (opts.preferExisting && hasExisting) {
    return String(existing);
  }

  const poidsDeclares = parseFloat(String(opts.poidsDeclares ?? ''));
  if (!Number.isNaN(poidsDeclares) && poidsDeclares > 0) {
    return String(poidsDeclares);
  }

  if (declares > 0) {
    return String(declares * capacite);
  }

  if (hasExisting) {
    return String(existing);
  }

  return '0';
};

const computeNbSacsAcceptes = (
  nbSacsSains: string | number | undefined,
  nbSacsRecond: string | number | undefined,
  nbSacsDeclares: string | number | undefined,
  nbSacsAcceptesExistant?: string | number | null,
): string => {
  const sains = parseInt(String(nbSacsSains ?? ''), 10) || 0;
  const recond = parseInt(String(nbSacsRecond ?? ''), 10) || 0;
  const declares = parseInt(String(nbSacsDeclares ?? ''), 10) || 0;

  if (sains > 0 || recond > 0) {
    return String(sains + recond);
  }
  if (declares > 0) {
    return String(declares);
  }
  const existing = parseInt(String(nbSacsAcceptesExistant ?? ''), 10);
  if (!Number.isNaN(existing) && existing > 0) {
    return String(existing);
  }
  return '0';
};

/** Extrait l'objet pesée depuis une réponse API éventuellement encapsulée */
const unwrapPeseePayload = (response: unknown): Record<string, unknown> => {
  let current: any = response;
  for (let i = 0; i < 3; i++) {
    if (
      current &&
      typeof current === 'object' &&
      current.data &&
      typeof current.data === 'object' &&
      !Array.isArray(current.data) &&
      (current.success !== undefined || current.message !== undefined || !('reference' in current))
    ) {
      // Si data ressemble à une pesée, on la prend
      if ('reference' in current.data || 'poids_brut' in current.data || 'nb_sacs_declares' in current.data || 'id' in current.data) {
        current = current.data;
        break;
      }
      current = current.data;
      continue;
    }
    break;
  }
  return (current && typeof current === 'object' ? current : {}) as Record<string, unknown>;
};

interface PeseeComplete {
  id: number;
  reference: string;
  date_heure: string;
  statut: string;
  type_pesee: 'reception' | 'expedition' | 'transfert';
  processus?: 'standard' | 'inverted' | string | null;
  poids_brut: number;
  poids_tare: number;
  poids_net: number;
  camion?: string;
  site: number;
  created_at: string;
  updated_at: string;
  produit?: {
    id: number;
    nom: string;
  };
  produit_info?: {
    id: number;
    nom: string;
    conditionnement_info?: {
      id: number;
      nom: string;
      poids_tare?: number;
    };
  };
  // Champs optionnels pour les détails supplémentaires
  provenance?: string;
  connaissement?: string;
  numero_transfert?: string;
  sticker?: string;
  numero_lot?: string;
  observations?: string;
  conditionnement?: string;
  conditionnement_info?: {
    id: number;
    nom: string;
    poids_tare?: number;
  };
  nb_sacs_declares?: number;
  nb_sacs_sains?: number;
  nb_sacs_recond?: number;
  nb_sacs_humides?: number;
  nb_sacs_dechires?: number;
  nb_sacs_rejetes?: number;
  nb_sacs_acceptes?: number;
  poids_acceptes?: number;
  poids_declares?: number;
  // Champs d'analyse qualité
  grainnage?: string;
  humidite?: number;
  quality?: number;
  quality_level?: string;
  quality_decision?: string;
  refraction_sacherie?: number;
  analyse_qualite?: {
    id?: number;
    grainnage?: string;
    humidite?: number;
    quality?: number;
    quality_level?: string;
    quality_decision?: string;
    refraction_sacherie?: number;
    observations?: string;
  };
  // Champs de stockage
  magasin?: {
    id: number;
    nom: string;
  };
  magasin_id?: number;
  destination?: string;
  camion_info?: {
    id: number;
    immatriculation: string;
  };
  remorque_info?: {
    id: number;
    immatriculation: string;
  };
  transporteur_info?: {
    id: number;
    nom: string;
  };
  chauffeur_info?: {
    id: number;
    nom: string;
    prenom: string;
  };
  convoyeur_info?: {
    id: number;
    nom: string;
    prenom: string;
  };
  client_info?: {
    id: number;
    nom: string;
  };
  fournisseur_info?: {
    id: number;
    nom: string;
  };
  exportateur_info?: {
    id: number;
    nom: string;
  };
}

interface PeseeModificationModalProps {
  isOpen: boolean;
  pesee: PeseeComplete | null;
  onClose: () => void;
  onSuccess?: (message: string) => void;
}

const PeseeModificationModal: React.FC<PeseeModificationModalProps> = ({
  isOpen,
  pesee,
  onClose,
  onSuccess
}) => {
  const { hasAnyRole, hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('basic');
  const [loadingRelations, setLoadingRelations] = useState(false);
  const [loadingPesee, setLoadingPesee] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [peseeDetails, setPeseeDetails] = useState<PeseeComplete | null>(null);
  const [saving, setSaving] = useState(false);
  /** false = l'utilisateur a saisi le poids net à la main ; true = recalcul auto */
  const [autoRecalcPoidsNet, setAutoRecalcPoidsNet] = useState(true);
  /** true après modification manuelle des compteurs de sacs → recalcul poids acceptés */
  const [sacsFieldsTouched, setSacsFieldsTouched] = useState(false);

  const currentPesee = peseeDetails ?? pesee;

  const needsAdmin = currentPesee ? requiresAdminToEditPesee(currentPesee.statut) : false;
  const hasRoleForStatus = needsAdmin
    ? hasAnyRole([...ADMIN_MODIFY_COMPLETED_ROLES])
    : hasAnyRole([...MODIFY_PESSE_ROLES]);
  const canModify = hasRoleForStatus && hasPermission(PERMISSIONS.WEIGHING_EDIT);
  const isEditable = currentPesee ? isPeseeEditable(currentPesee.statut) : false;
  
  // États pour les données des relations
  const [trailers, setTrailers] = useState<any[]>([]);
  const [carriers, setCarriers] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [exporters, setExporters] = useState<any[]>([]);
  const [conditionnements, setConditionnements] = useState<any[]>([]);
  const [magasins, setMagasins] = useState<any[]>([]);

  // États pour les données modifiées
  const [modifiedData, setModifiedData] = useState<any>({
    type_pesee: '',
    statut: '',
    processus: 'standard',
    produit_id: '',
    conditionnement_id: '',
    camion_id: '',
    remorque_id: '',
    transporteur_id: '',
    chauffeur_id: '',
    convoyeur_id: '',
    client_id: '',
    fournisseur_id: '',
    exportateur_id: '',
    provenance: '',
    connaissement: '',
    numero_transfert: '',
    sticker: '',
    numero_lot: '',
    observations: '',
    poids_brut: '',
    poids_tare: '',
    poids_net: '',
    grainnage: '',
    humidite: '',
    quality: '',
    quality_level: '',
    quality_decision: '',
    refraction_sacherie: '',
    analyse_observations: '',
    nb_sacs_declares: '',
    nb_sacs_sains: '',
    nb_sacs_recond: '',
    nb_sacs_humides: '',
    nb_sacs_dechires: '',
    nb_sacs_rejetes: '',
    nb_sacs_acceptes: '',
    poids_acceptes: '',
    poids_declares: '',
    magasin_id: '',
    destination: ''
  });

  // Charger les données des relations
  const loadRelationsData = async () => {
    setLoadingRelations(true);
    try {
      // Charger les remorques - page_size élevé pour récupérer tous les résultats
      const trailersResponse = await weighingService.getRemorques({ page_size: 1000 });
      if (trailersResponse && trailersResponse.results) {
        setTrailers(trailersResponse.results);
      }

      // Charger les transporteurs - page_size élevé pour récupérer tous les résultats
      const carriersResponse = await weighingService.getTransporteurs({ page_size: 1000 });
      if (carriersResponse && carriersResponse.results) {
        setCarriers(carriersResponse.results);
      }

      // Charger les clients - page_size élevé pour récupérer tous les résultats
      const clientsResponse = await weighingService.getClients({ page_size: 1000 });
      if (clientsResponse && clientsResponse.results) {
        setClients(clientsResponse.results);
      }

      // Charger les exportateurs - page_size élevé pour récupérer tous les résultats
      const exportersResponse = await weighingService.getExportateurs({ page_size: 1000 });
      if (exportersResponse && exportersResponse.results) {
        setExporters(exportersResponse.results);
      }

      // Charger les conditionnements - page_size élevé pour récupérer tous les résultats
      const conditionnementsResponse = await weighingService.getConditionnements({ page_size: 1000 });
      if (conditionnementsResponse && conditionnementsResponse.results) {
        console.log('Conditionnements chargés:', conditionnementsResponse.results);
        setConditionnements(conditionnementsResponse.results);
      }

      // Charger les magasins - page_size élevé pour récupérer tous les résultats
      const magasinsResponse = await weighingService.getMagasins({ page_size: 1000 });
      if (magasinsResponse && magasinsResponse.results) {
        console.log('Magasins chargés:', magasinsResponse.results);
        setMagasins(magasinsResponse.results);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données de relations:', error);
    } finally {
      setLoadingRelations(false);
    }
  };

  // Charger les détails complets de la pesée à l'ouverture
  useEffect(() => {
    if (!isOpen || !pesee?.id) {
      setPeseeDetails(null);
      setLoadError('');
      return;
    }

    let cancelled = false;

    const loadPeseeDetails = async () => {
      setLoadingPesee(true);
      setLoadError('');
      try {
        const peseeId = typeof pesee.id === 'string' ? parseInt(pesee.id, 10) : Number(pesee.id);
        const response = await weighingService.getPesée(peseeId);
        const data = unwrapPeseePayload(response) as unknown as PeseeComplete;
        if (!cancelled) {
          setPeseeDetails(data);
        }
      } catch {
        if (!cancelled) {
          setPeseeDetails(pesee);
          setLoadError('Impossible de charger tous les détails. Données partielles utilisées.');
        }
      } finally {
        if (!cancelled) {
          setLoadingPesee(false);
        }
      }
    };

    loadPeseeDetails();
    return () => {
      cancelled = true;
    };
  }, [isOpen, pesee?.id]);

  const initializeModifiedData = useCallback((source: PeseeComplete) => {
    const nbSacsDeclares = source.nb_sacs_declares ?? 0;
    const nbSacsSains = source.nb_sacs_sains ?? 0;
    const nbSacsRecond = source.nb_sacs_recond ?? 0;
    const nbSacsAcceptes = computeNbSacsAcceptes(
      nbSacsSains,
      nbSacsRecond,
      nbSacsDeclares,
      source.nb_sacs_acceptes,
    );

    const conditionnementCapacite =
      source.produit_info?.conditionnement_info?.poids_tare ||
      source.conditionnement_info?.poids_tare ||
      50;

    const poidsAcceptes = computePoidsAcceptes({
      nbSacsSains,
      nbSacsRecond,
      nbSacsDeclares,
      conditionnementCapacite,
      poidsAcceptesExistant: source.poids_acceptes,
      poidsDeclares: source.poids_declares,
      preferExisting: true,
    });

    const analyse = source.analyse_qualite;

    setAutoRecalcPoidsNet(true);
    setSacsFieldsTouched(false);
    setModifiedData({
      type_pesee: source.type_pesee || '',
      statut: source.statut || '',
      processus: source.processus === 'inverted' ? 'inverted' : 'standard',
      produit_id: source.produit?.id || source.produit_info?.id || '',
      conditionnement_id:
        source.conditionnement_info?.id || source.produit_info?.conditionnement_info?.id || '',
      camion_id: source.camion_info?.id || '',
      remorque_id: source.remorque_info?.id || '',
      transporteur_id: source.transporteur_info?.id || '',
      chauffeur_id: source.chauffeur_info?.id || '',
      convoyeur_id: source.convoyeur_info?.id || '',
      client_id: source.client_info?.id || '',
      fournisseur_id: source.fournisseur_info?.id || '',
      exportateur_id: source.exportateur_info?.id || '',
      provenance: source.provenance || '',
      connaissement: source.connaissement || '',
      numero_transfert: source.numero_transfert || '',
      sticker: source.sticker || '',
      numero_lot: source.numero_lot || '',
      observations: source.observations || '',
      poids_brut: toFormValue(source.poids_brut),
      poids_tare: toFormValue(source.poids_tare),
      poids_net: toFormValue(source.poids_net),
      grainnage: analyse?.grainnage || source.grainnage || '',
      humidite: toFormValue(analyse?.humidite ?? source.humidite),
      quality: toFormValue(analyse?.quality ?? source.quality),
      quality_level: analyse?.quality_level || source.quality_level || '',
      quality_decision: analyse?.quality_decision || source.quality_decision || '',
      refraction_sacherie: toFormValue(analyse?.refraction_sacherie ?? source.refraction_sacherie),
      analyse_observations: analyse?.observations || '',
      nb_sacs_declares: toFormValue(nbSacsDeclares),
      nb_sacs_sains: toFormValue(nbSacsSains),
      nb_sacs_recond: toFormValue(nbSacsRecond),
      nb_sacs_humides: toFormValue(source.nb_sacs_humides),
      nb_sacs_dechires: toFormValue(source.nb_sacs_dechires),
      nb_sacs_rejetes: toFormValue(source.nb_sacs_rejetes),
      nb_sacs_acceptes: nbSacsAcceptes,
      poids_acceptes: poidsAcceptes,
      poids_declares: toFormValue(source.poids_declares),
      magasin_id: source.magasin?.id || source.magasin_id || '',
      destination: source.destination || '',
    });
  }, []);

  // Initialiser les données modifiées quand la pesée change
  useEffect(() => {
    if (currentPesee) {
      initializeModifiedData(currentPesee);
    }
  }, [currentPesee, initializeModifiedData]);

  // Calcul automatique du poids net (sauf si saisie manuelle)
  useEffect(() => {
    if (!autoRecalcPoidsNet) return;
    if (modifiedData.poids_brut === '' || modifiedData.poids_brut == null) return;
    if (modifiedData.poids_tare === '' || modifiedData.poids_tare == null) return;

    const poidsNetCalcule = computePoidsNet(
      modifiedData.poids_brut,
      modifiedData.poids_tare,
      modifiedData.poids_acceptes ?? '0',
    );

    if (modifiedData.poids_net !== poidsNetCalcule) {
      setModifiedData((prev: any) => ({ ...prev, poids_net: poidsNetCalcule }));
    }
  }, [
    autoRecalcPoidsNet,
    modifiedData.poids_brut,
    modifiedData.poids_tare,
    modifiedData.poids_acceptes,
    modifiedData.poids_net,
  ]);

  // Calcul automatique des sacs acceptés (sains/recond, sinon déclarés)
  useEffect(() => {
    const nbSacsAcceptesCalcules = computeNbSacsAcceptes(
      modifiedData.nb_sacs_sains,
      modifiedData.nb_sacs_recond,
      modifiedData.nb_sacs_declares,
      modifiedData.nb_sacs_acceptes,
    );
    if (modifiedData.nb_sacs_acceptes !== nbSacsAcceptesCalcules) {
      setModifiedData((prev: any) => ({ ...prev, nb_sacs_acceptes: nbSacsAcceptesCalcules }));
    }
  }, [modifiedData.nb_sacs_sains, modifiedData.nb_sacs_recond, modifiedData.nb_sacs_declares]);

  // Calcul automatique du poids accepté (prend en compte les sacs déclarés)
  useEffect(() => {
    const capacite = getConditionnementCapacite(
      modifiedData.conditionnement_id,
      conditionnements,
      50,
    );
    const poidsAcceptesCalcules = computePoidsAcceptes({
      nbSacsSains: modifiedData.nb_sacs_sains,
      nbSacsRecond: modifiedData.nb_sacs_recond,
      nbSacsDeclares: modifiedData.nb_sacs_declares,
      conditionnementCapacite: capacite,
      poidsAcceptesExistant: modifiedData.poids_acceptes,
      poidsDeclares: modifiedData.poids_declares,
      // Tant que l'utilisateur n'a pas touché les sacs, ne pas écraser le poids stocké
      preferExisting: !sacsFieldsTouched,
    });

    if (modifiedData.poids_acceptes !== poidsAcceptesCalcules) {
      setModifiedData((prev: any) => ({ ...prev, poids_acceptes: poidsAcceptesCalcules }));
      setAutoRecalcPoidsNet(true);
    }
  }, [
    modifiedData.nb_sacs_sains,
    modifiedData.nb_sacs_recond,
    modifiedData.nb_sacs_declares,
    modifiedData.poids_declares,
    modifiedData.conditionnement_id,
    conditionnements,
    sacsFieldsTouched,
  ]);

  // Charger les données des relations au montage
  useEffect(() => {
    if (isOpen) {
      loadRelationsData();
    }
  }, [isOpen]);

  // Gérer les changements de données
  const handleDataChange = (field: string, value: any) => {
    if (field === 'poids_net') {
      setAutoRecalcPoidsNet(false);
    } else if (
      field === 'poids_brut' ||
      field === 'poids_tare' ||
      field === 'poids_acceptes' ||
      field === 'nb_sacs_declares' ||
      field === 'nb_sacs_sains' ||
      field === 'nb_sacs_recond'
    ) {
      setAutoRecalcPoidsNet(true);
    }
    if (
      field === 'nb_sacs_declares' ||
      field === 'nb_sacs_sains' ||
      field === 'nb_sacs_recond'
    ) {
      setSacsFieldsTouched(true);
    }
    setModifiedData((prev: any) => {
      if (field === 'processus') {
        const brutVal = parseFloat(String(prev.poids_brut || '')) || 0;
        const tareVal = parseFloat(String(prev.poids_tare || '')) || 0;
        const hasBrut = brutVal > 0;
        const hasTare = tareVal > 0;
        if (value === 'inverted' && hasBrut && !hasTare) {
          return {
            ...prev,
            processus: value,
            poids_tare: prev.poids_brut,
            poids_brut: '',
            poids_net: '',
            statut: 'TARE_ENREGISTREE',
          };
        }
        if (value === 'standard' && hasTare && !hasBrut) {
          return {
            ...prev,
            processus: value,
            poids_brut: prev.poids_tare,
            poids_tare: '',
            poids_net: '',
            statut: prev.statut === 'TARE_ENREGISTREE' ? 'BRUT_ENREGISTRE' : prev.statut,
          };
        }
      }
      return {
        ...prev,
        [field]: value
      };
    });
  };

  /** Inverse brut ↔ tare puis recalcule le poids net */
  const handleSwapBrutTare = () => {
    setAutoRecalcPoidsNet(true);
    setModifiedData((prev: any) => {
      const nextBrut = prev.poids_tare ?? '';
      const nextTare = prev.poids_brut ?? '';
      return {
        ...prev,
        poids_brut: nextBrut,
        poids_tare: nextTare,
        poids_net: computePoidsNet(nextBrut, nextTare, prev.poids_acceptes ?? '0'),
      };
    });
  };

  /** Force le recalcul du poids net à partir de brut / tare / sacs */
  const handleRecalcPoidsNet = () => {
    setAutoRecalcPoidsNet(true);
    setModifiedData((prev: any) => ({
      ...prev,
      poids_net: computePoidsNet(
        prev.poids_brut ?? '',
        prev.poids_tare ?? '',
        prev.poids_acceptes ?? '0',
      ),
    }));
  };

  const handleSave = async () => {
    if (!currentPesee) return;

    if (!isEditable) {
      alert('Cette pesée ne peut plus être modifiée (statut refoulé ou annulé).');
      return;
    }

    setSaving(true);
    try {
      const capacite = getConditionnementCapacite(
        modifiedData.conditionnement_id,
        conditionnements,
        50,
      );
      const poidsAcceptesSync = computePoidsAcceptes({
        nbSacsSains: modifiedData.nb_sacs_sains,
        nbSacsRecond: modifiedData.nb_sacs_recond,
        nbSacsDeclares: modifiedData.nb_sacs_declares,
        conditionnementCapacite: capacite,
        poidsAcceptesExistant: modifiedData.poids_acceptes,
        poidsDeclares: modifiedData.poids_declares,
        preferExisting: false,
      });
      const poidsNetSync = computePoidsNet(
        modifiedData.poids_brut ?? '',
        modifiedData.poids_tare ?? '',
        poidsAcceptesSync,
      );
      const nbSacsAcceptesSync = computeNbSacsAcceptes(
        modifiedData.nb_sacs_sains,
        modifiedData.nb_sacs_recond,
        modifiedData.nb_sacs_declares,
        modifiedData.nb_sacs_acceptes,
      );

      const rawData = {
        type_pesee: modifiedData.type_pesee,
        statut: modifiedData.statut || undefined,
        processus: modifiedData.processus || undefined,
        provenance: modifiedData.provenance,
        connaissement: modifiedData.connaissement,
        numero_transfert: modifiedData.numero_transfert,
        sticker: modifiedData.sticker,
        poids_brut: modifiedData.poids_brut === '' || modifiedData.poids_brut == null
          ? null
          : parseOptionalFloat(modifiedData.poids_brut),
        poids_tare: modifiedData.poids_tare === '' || modifiedData.poids_tare == null
          ? null
          : parseOptionalFloat(modifiedData.poids_tare),
        poids_net: parseOptionalFloat(poidsNetSync) ?? null,
        poids_acceptes: parseOptionalFloat(poidsAcceptesSync),
        nb_sacs_declares: parseOptionalInt(modifiedData.nb_sacs_declares),
        nb_sacs_sains: parseOptionalInt(modifiedData.nb_sacs_sains),
        nb_sacs_recond: parseOptionalInt(modifiedData.nb_sacs_recond),
        nb_sacs_humides: parseOptionalInt(modifiedData.nb_sacs_humides),
        nb_sacs_dechires: parseOptionalInt(modifiedData.nb_sacs_dechires),
        nb_sacs_rejetes: parseOptionalInt(modifiedData.nb_sacs_rejetes),
        nb_sacs_acceptes: parseOptionalInt(nbSacsAcceptesSync),
        magasin: parseOptionalInt(modifiedData.magasin_id),
        destination: modifiedData.destination,
        numero_lot: modifiedData.numero_lot,
        observations: modifiedData.observations,
        remorque: parseOptionalInt(modifiedData.remorque_id),
        transporteur: parseOptionalInt(modifiedData.transporteur_id),
        client: parseOptionalInt(modifiedData.client_id),
        exportateur: parseOptionalInt(modifiedData.exportateur_id),
        analyse_qualite: {
          grainnage: modifiedData.grainnage || undefined,
          humidite: parseOptionalFloat(modifiedData.humidite),
          quality: parseOptionalFloat(modifiedData.quality),
          quality_level: modifiedData.quality_level || undefined,
          quality_decision: modifiedData.quality_decision || undefined,
          refraction_sacherie: parseOptionalFloat(modifiedData.refraction_sacherie),
          observations: modifiedData.analyse_observations || undefined,
        },
      };

      // Ne pas envoyer analyse_qualite si tous les champs sont vides
      const analyseEntries = Object.entries(rawData.analyse_qualite).filter(
        ([, value]) => value !== undefined && value !== null && value !== ''
      );
      if (analyseEntries.length === 0) {
        delete (rawData as { analyse_qualite?: unknown }).analyse_qualite;
      } else {
        rawData.analyse_qualite = Object.fromEntries(analyseEntries) as typeof rawData.analyse_qualite;
      }

      // null doit passer pour poids_brut / poids_tare / poids_net
      // (repasser en parcours inversé vide le brut et place le poids en tare)
      const nullableWeightFields = new Set(['poids_brut', 'poids_tare', 'poids_net']);
      const dataToSend = Object.fromEntries(
        Object.entries(rawData).filter(([key, value]) => {
          if (nullableWeightFields.has(key) && value === null) return true;
          if (value === undefined || value === null || value === '') return false;
          if (typeof value === 'string' && value.trim() === '') return false;
          return true;
        })
      );

      const peseeId =
        typeof currentPesee.id === 'string'
          ? parseInt(currentPesee.id, 10)
          : Number(currentPesee.id);

      const response = await weighingService.updatePesee(peseeId, dataToSend);

      if (response.success !== false) {
        const successMessage = response.message || 'Pesée modifiée avec succès';
        if (onSuccess) {
          onSuccess(successMessage);
        }
        onClose();
      } else {
        alert(formatApiError({ response: { data: response } }));
      }
    } catch (error: unknown) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !pesee) return null;
  if (!currentPesee) return null;

  const modalOverlayProps = {
    'data-history-modal': 'true' as const,
    onMouseDown: (event: React.MouseEvent) => event.stopPropagation(),
  };

  // Si l'utilisateur n'a pas les permissions, afficher un message d'erreur
  if (!canModify) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        {...modalOverlayProps}
      >
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Accès refusé
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
          
          <div className="px-6 py-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <XMarkIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Permissions insuffisantes
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Vous n'avez pas les permissions nécessaires pour modifier cette pesée.
                <br />
                <br />
                {needsAdmin ? (
                  <>
                    <strong>Pesée complète — rôles admin requis :</strong>
                    <br />
                    • Super Administrateur / Admin Plateforme
                    <br />
                    • Propriétaire / Administrateur Tenant
                    <br />
                    • Administrateur de Site
                  </>
                ) : (
                  <>
                    <strong>Rôles autorisés :</strong>
                    <br />
                    • Super Administrateur / Admin / Support Plateforme
                    <br />
                    • Administrateur / Gestionnaire / Propriétaire Tenant
                    <br />
                    • Administrateur / Gestionnaire / Superviseur de Site
                  </>
                )}
              </p>
              <button
                onClick={onClose}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isEditable) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        {...modalOverlayProps}
      >
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Modification impossible</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
          <div className="px-6 py-6 text-center">
            <p className="text-sm text-gray-600 mb-6">
              Cette pesée ne peut plus être modifiée car son statut est{' '}
              <strong>{currentPesee?.statut}</strong> (refoulée ou annulée).
            </p>
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      {...modalOverlayProps}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              Modifier la pesée {currentPesee.reference}
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getWeighingProcessBadgeClass({ processus: modifiedData.processus })}`}>
                {getWeighingProcessLabel({ processus: modifiedData.processus })}
              </span>
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Onglets */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <nav className="flex space-x-8">
            {[
              { id: 'basic', label: 'Informations de base' },
              { id: 'weighing', label: 'Informations de pesée' },
              { id: 'quality_analysis', label: 'Analyse qualité' },
              { id: 'storage', label: 'Informations de stockage' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Contenu des onglets */}
        <div className="px-6 py-6">
          {(loadingRelations || loadingPesee) && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              <span className="ml-2 text-gray-600">
                {loadingPesee ? 'Chargement de la pesée...' : 'Chargement des données...'}
              </span>
            </div>
          )}

          {loadError && !loadingPesee && (
            <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              {loadError}
            </div>
          )}
          
          {!loadingRelations && !loadingPesee && activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900">Informations de base</h4>
                <div className="text-sm text-gray-500">Étape 1/4</div>
              </div>
              
              {/* Note d'information */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-start">
                  <div className="text-blue-600 text-sm">
                    <strong>Note :</strong> Certains champs (produit, conditionnement, véhicule, chauffeur, convoyeur, fournisseur) ne peuvent pas être modifiés via cette interface.
                  </div>
                </div>
              </div>
              
              {/* Référence - Non modifiable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Référence</label>
                <input
                  type="text"
                  value={currentPesee.reference}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  readOnly
                  disabled
                />
                <p className="text-xs text-gray-500 mt-1">La référence ne peut pas être modifiée</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <select
                    value={modifiedData.statut || ''}
                    onChange={(e) => handleDataChange('statut', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="EN_COURS">En cours</option>
                    <option value="BRUT_ENREGISTRE">Brut enregistré</option>
                    <option value="EN_ATTENTE_TARE">En attente tare</option>
                    <option value="TARE_ENREGISTREE">Tare enregistrée</option>
                    <option value="COMPLETEE">Complétée</option>
                    <option value="REFOULEE">Refoulée</option>
                    <option value="ANNULEE">Annulée</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de pesée</label>
                  <select 
                    value={modifiedData.type_pesee || ''}
                    onChange={(e) => handleDataChange('type_pesee', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="reception">Réception</option>
                    <option value="expedition">Expédition</option>
                    <option value="transfert">Transfert</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parcours de pesée</label>
                  <select
                    value={modifiedData.processus || 'standard'}
                    onChange={(e) => handleDataChange('processus', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="standard">Parcours standard (brut → tare)</option>
                    <option value="inverted">Parcours inversé (tare → brut)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {modifiedData.processus === 'inverted'
                      ? 'Pour une pesée inversée mal passée en standard : le poids unique est remis dans la tare et le statut devient « Tare enregistrée ».'
                      : 'Parcours standard : brut (chargé) puis tare (vide).'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Produit</label>
                  <input
                    type="text"
                    value={currentPesee.produit?.nom || currentPesee.produit_info?.nom || 'Non défini'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    readOnly
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">Le produit ne peut pas être modifié</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sac/Conditionnement</label>
                  <input
                    type="text"
                    value={currentPesee.produit_info?.conditionnement_info?.nom || currentPesee.conditionnement_info?.nom || 'Non défini'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    readOnly
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">Le conditionnement ne peut pas être modifié</p>
                </div>
              </div>

              {/* Informations véhicule */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 text-sm mb-3">INFORMATIONS VÉHICULE</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Véhicule</label>
                    <input
                      type="text"
                      value={currentPesee.camion || currentPesee.camion_info?.immatriculation || 'Non défini'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                      readOnly
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">Le véhicule ne peut pas être modifié</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Remorque</label>
                    <SearchableSelect
                      options={trailers.map(t => ({ 
                        value: t.id.toString(), 
                        label: `${t.immatriculation} - ${t.marque} (Capacité: ${t.capacite || ''}kg)` 
                      }))}
                      value={modifiedData.remorque_id ? modifiedData.remorque_id.toString() : ''}
                      onChange={(value) => handleDataChange('remorque_id', value)}
                      placeholder="Sélectionner une remorque"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transporteur</label>
                    <SearchableSelect
                      options={carriers.map(c => ({ 
                        value: c.id.toString(), 
                        label: `${c.nom} - ${c.zone || ''}` 
                      }))}
                      value={modifiedData.transporteur_id ? modifiedData.transporteur_id.toString() : ''}
                      onChange={(value) => handleDataChange('transporteur_id', value)}
                      placeholder="Sélectionner un transporteur"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Chauffeur</label>
                    <input
                      type="text"
                      value={currentPesee.chauffeur_info ? `${currentPesee.chauffeur_info.nom} ${currentPesee.chauffeur_info.prenom}` : 'Non défini'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                      readOnly
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">Le chauffeur ne peut pas être modifié</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Convoyeur</label>
                    <input
                      type="text"
                      value={currentPesee.convoyeur_info ? `${currentPesee.convoyeur_info.nom} ${currentPesee.convoyeur_info.prenom}` : 'Non défini'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                      readOnly
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">Le convoyeur ne peut pas être modifié</p>
                  </div>
                </div>
              </div>

              {/* Informations client/fournisseur */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-medium text-blue-900 text-sm mb-3">CLIENT / FOURNISSEUR</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">Client</label>
                    <SearchableSelect
                      options={clients.map(c => ({ value: c.id.toString(), label: c.nom }))}
                      value={modifiedData.client_id ? modifiedData.client_id.toString() : ''}
                      onChange={(value) => handleDataChange('client_id', value)}
                      placeholder="Sélectionner un client"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">Fournisseur</label>
                    <input
                      type="text"
                      value={currentPesee.fournisseur_info?.nom || 'Non défini'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                      readOnly
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">Le fournisseur ne peut pas être modifié</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">Exportateur</label>
                    <SearchableSelect
                      options={exporters.map(e => ({ value: e.id.toString(), label: e.nom }))}
                      value={modifiedData.exportateur_id ? modifiedData.exportateur_id.toString() : ''}
                      onChange={(value) => handleDataChange('exportateur_id', value)}
                      placeholder="Sélectionner un exportateur"
                    />
                  </div>
                </div>
              </div>

              {/* Provenance */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h5 className="font-medium text-green-900 text-sm mb-3">PROVENANCE</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-1">Provenance</label>
                    <input
                      type="text"
                      value={modifiedData.provenance || ''}
                      onChange={(e) => handleDataChange('provenance', e.target.value)}
                      placeholder="Lieu de provenance"
                      className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-1">Connaissement</label>
                    <input
                      type="text"
                      value={modifiedData.connaissement || ''}
                      onChange={(e) => handleDataChange('connaissement', e.target.value)}
                      placeholder="Numéro de connaissement"
                      className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Transfert et Sticker */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h5 className="font-medium text-purple-900 text-sm mb-3">TRANSFERT ET STICKER</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-purple-800 mb-1">Numéro de Transfert</label>
                    <input
                      type="text"
                      value={modifiedData.numero_transfert || ''}
                      onChange={(e) => handleDataChange('numero_transfert', e.target.value)}
                      placeholder="Numéro de transfert"
                      className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-purple-800 mb-1">Sticker</label>
                    <input
                      type="text"
                      value={modifiedData.sticker || ''}
                      onChange={(e) => handleDataChange('sticker', e.target.value)}
                      placeholder="Numéro de sticker"
                      className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loadingRelations && !loadingPesee && activeTab === 'weighing' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900">Informations de pesée</h4>
                <div className="text-sm text-gray-500">Étape 2/4</div>
              </div>

              {/* Première pesée */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-medium text-blue-900 text-sm mb-3">PREMIÈRE PESÉE (À CHARGE)</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">Poids brut (kg)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={modifiedData.poids_brut || ''}
                      onChange={(e) => handleDataChange('poids_brut', e.target.value)}
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">Heure d'entrée</label>
                    <input
                      type="datetime-local"
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Inversion brut ↔ tare */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-sm text-amber-900">
                  Valeurs brut et tare inversées par erreur ? Échangez-les puis le poids net sera recalculé.
                </p>
                <button
                  type="button"
                  onClick={handleSwapBrutTare}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-amber-950 bg-white border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors shrink-0"
                >
                  <ArrowsRightLeftIcon className="h-4 w-4" />
                  Inverser brut ↔ tare
                </button>
              </div>

              {/* Deuxième pesée */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h5 className="font-medium text-green-900 text-sm mb-3">DEUXIÈME PESÉE (À VIDE)</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-1">Poids tare (kg)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={modifiedData.poids_tare || ''}
                      onChange={(e) => handleDataChange('poids_tare', e.target.value)}
                      className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-1">Heure de sortie</label>
                    <input
                      type="datetime-local"
                      className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Détails des sacs */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 text-sm mb-3">DÉTAILS DES SACS</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs déclarés</label>
                    <input
                      type="number"
                      value={modifiedData.nb_sacs_declares ?? ''}
                      onChange={(e) => handleDataChange('nb_sacs_declares', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs sains</label>
                    <input
                      type="number"
                      value={modifiedData.nb_sacs_sains ?? ''}
                      onChange={(e) => handleDataChange('nb_sacs_sains', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs Recond</label>
                    <input
                      type="number"
                      value={modifiedData.nb_sacs_recond ?? ''}
                      onChange={(e) => handleDataChange('nb_sacs_recond', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs Humides</label>
                    <input
                      type="number"
                      value={modifiedData.nb_sacs_humides || ''}
                      onChange={(e) => handleDataChange('nb_sacs_humides', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs déchirés</label>
                    <input
                      type="number"
                      value={modifiedData.nb_sacs_dechires || ''}
                      onChange={(e) => handleDataChange('nb_sacs_dechires', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs rejetés</label>
                    <input
                      type="number"
                      value={modifiedData.nb_sacs_rejetes || ''}
                      onChange={(e) => handleDataChange('nb_sacs_rejetes', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nb de sacs acceptés 
                      <span className="text-xs text-gray-500 ml-1">(calculé automatiquement)</span>
                    </label>
                    <input
                      type="number"
                      value={modifiedData.nb_sacs_acceptes ?? ''}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      = sains + recond, ou sacs déclarés si sains/recond vides
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Poids accepté (kg)
                      <span className="text-xs text-gray-500 ml-1">(calculé automatiquement)</span>
                    </label>
                    <input
                      type="number"
                      value={modifiedData.poids_acceptes ?? ''}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      = (sains + recond × 4) × capacité, ou déclarés × capacité si sains/recond vides
                    </p>
                  </div>
                </div>
              </div>

              {/* Calcul du poids net */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <h5 className="font-medium text-orange-900 text-sm">POIDS NET</h5>
                  <button
                    type="button"
                    onClick={handleRecalcPoidsNet}
                    className="inline-flex items-center gap-1.5 self-start px-3 py-1.5 text-xs font-medium text-orange-900 bg-white border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors"
                  >
                    <ArrowPathIcon className="h-3.5 w-3.5" />
                    Recalculer le poids net
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-orange-800 mb-1">Poids sacs acceptés (kg)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={modifiedData.poids_acceptes || ''}
                      readOnly
                      className="w-full px-3 py-2 border border-orange-300 rounded-lg bg-orange-50 text-orange-700"
                      placeholder="Calculé automatiquement"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-orange-800 mb-1">
                      Poids net (kg)
                      {!autoRecalcPoidsNet && (
                        <span className="ml-2 text-xs font-normal text-orange-600">(saisie manuelle)</span>
                      )}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={modifiedData.poids_net || ''}
                      onChange={(e) => handleDataChange('poids_net', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg font-semibold focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        autoRecalcPoidsNet
                          ? 'border-orange-300 bg-white text-orange-900'
                          : 'border-orange-500 bg-white text-orange-950 ring-1 ring-orange-300'
                      }`}
                      placeholder="Saisir ou recalculer"
                    />
                  </div>
                  <div className="flex items-end">
                    <div className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                      Poids Net = Poids Brut − (Poids Tare + Poids Sacs). Modifiable à la main si besoin.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loadingRelations && !loadingPesee && activeTab === 'quality_analysis' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900">Analyse qualité</h4>
                <div className="text-sm text-gray-500">Étape 3/4</div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-blue-800 text-sm">
                  <strong>Modification autorisée :</strong> vous pouvez corriger les critères qualité
                  de cette pesée. Le score qualité sera recalculé automatiquement à l&apos;enregistrement.
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 text-sm mb-3">CRITÈRES QUALITÉ</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Grainnage</label>
                    <input
                      type="text"
                      value={modifiedData.grainnage || ''}
                      onChange={(e) => handleDataChange('grainnage', e.target.value)}
                      placeholder="Ex: 180/200"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Humidité (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={modifiedData.humidite || ''}
                      onChange={(e) => handleDataChange('humidite', e.target.value)}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Qualité (valeur)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={modifiedData.quality || ''}
                      onChange={(e) => handleDataChange('quality', e.target.value)}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Niveau de qualité</label>
                    <select
                      value={modifiedData.quality_level || ''}
                      onChange={(e) => handleDataChange('quality_level', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">Sélectionner</option>
                      <option value="EXCELLENT">Excellent</option>
                      <option value="BON">Bon</option>
                      <option value="MOYEN">Moyen</option>
                      <option value="FAIBLE">Faible</option>
                      <option value="TRES_FAIBLE">Très faible</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Réfraction sacherie</label>
                    <input
                      type="number"
                      step="0.01"
                      value={modifiedData.refraction_sacherie || ''}
                      onChange={(e) => handleDataChange('refraction_sacherie', e.target.value)}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Décision qualité</label>
                    <select
                      value={modifiedData.quality_decision || ''}
                      onChange={(e) => handleDataChange('quality_decision', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">Sélectionner</option>
                      <option value="ACCEPTE">Accepté</option>
                      <option value="ACCEPTE_RESERVE">Accepté avec réserve</option>
                      <option value="REFUSE">Refusé</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Observations qualité
                    </label>
                    <textarea
                      rows={3}
                      value={modifiedData.analyse_observations || ''}
                      onChange={(e) => handleDataChange('analyse_observations', e.target.value)}
                      placeholder="Observations sur la qualité du produit"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loadingRelations && !loadingPesee && activeTab === 'storage' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900">Informations de stockage</h4>
                <div className="text-sm text-gray-500">Étape 4/4</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Magasin</label>
                  <select
                    value={modifiedData.magasin_id || ''}
                    onChange={(e) => handleDataChange('magasin_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Sélectionner un magasin</option>
                    {magasins.map(magasin => (
                      <option key={magasin.id} value={magasin.id}>
                        {magasin.nom}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Destination</label>
                  <input
                    type="text"
                    value={modifiedData.destination || ''}
                    onChange={(e) => handleDataChange('destination', e.target.value)}
                    placeholder="Lieu de destination"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de lot</label>
                  <input
                    type="text"
                    value={modifiedData.numero_lot || ''}
                    onChange={(e) => handleDataChange('numero_lot', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observations</label>
                  <textarea
                    rows={3}
                    value={modifiedData.observations || ''}
                    onChange={(e) => handleDataChange('observations', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {activeTab === 'basic' && 'Informations de base'}
            {activeTab === 'weighing' && 'Informations de pesée'}
            {activeTab === 'quality_analysis' && 'Analyse qualité'}
            {activeTab === 'storage' && 'Informations de stockage'}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loadingRelations || loadingPesee || !isEditable}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {saving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeseeModificationModal;
